import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  FitnessCenter as FitnessIcon,
  CalendarToday as CalendarIcon,
  ArrowBack as ArrowBackIcon,
  BookmarkAdd as BookIcon,
  BookmarkRemove as UnbookIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { format, isToday, isTomorrow, isThisWeek } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ClassBookingDialog from '../components/ClassBookingDialog';
import api, { handleApiError } from '../utils/api';

const ScheduledClassDetailsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [scheduledClass, setScheduledClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [bookingDialog, setBookingDialog] = useState({
    open: false,
    classData: null
  });
  const [unbookingDialogOpen, setUnbookingDialogOpen] = useState(false);

  const loadClassDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/scheduled-classes/${id}`);
      setScheduledClass(response.data.data);
    } catch (error) {
      console.error('Error loading class details:', error);
      setError('Failed to load class details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadClassDetails();
  }, [loadClassDetails]);

  const handleQuickBook = () => {
    setBookingDialog({
      open: true,
      classData: scheduledClass
    });
  };

  const handleBookingDialogClose = () => {
    setBookingDialog({
      open: false,
      classData: null
    });
  };

  const handleBookingSuccess = () => {
    setSuccess('Successfully booked for class!');
    loadClassDetails(); // Refresh to show updated enrollment
    handleBookingDialogClose();
  };

  const handleUnbookClass = async () => {
    try {
      setActionLoading(true);
      await api.delete(`/scheduled-classes/${id}/unbook`);
      setSuccess('Successfully cancelled class booking!');
      setUnbookingDialogOpen(false);
      loadClassDetails(); // Refresh to show updated enrollment
    } catch (error) {
      setError(handleApiError(error));
      setUnbookingDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'success';
      case 'full': return 'warning';
      case 'in-progress': return 'info';
      case 'completed': return 'default';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getDateLabel = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isThisWeek(date)) return format(date, 'EEEE, MMM dd');
    return format(date, 'EEEE, MMM dd, yyyy');
  };

  const formatTime = (dateString) => {
    return format(new Date(dateString), 'HH:mm');
  };

  const formatDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMinutes = Math.round((end - start) / (1000 * 60));
    return `${durationMinutes} minutes`;
  };

  const getAvailabilityInfo = () => {
    if (!scheduledClass) return null;
    
    const enrolled = scheduledClass.currentEnrollment || 0;
    const capacity = scheduledClass.capacity;
    const available = capacity - enrolled;
    
    return {
      enrolled,
      capacity,
      available,
      isFull: available <= 0,
      isAlmostFull: available <= Math.ceil(capacity * 0.2) && available > 0
    };
  };

  const isUserBooked = () => {
    if (!scheduledClass || !user) return false;
    return scheduledClass.attendees?.some(attendee => 
      (attendee.user._id === user._id || attendee.user === user._id) &&
      (attendee.status === 'booked' || attendee.status === 'attended' || attendee.status === 'pending_payment')
    );
  };

  const canBookClass = () => {
    if (!scheduledClass || !user) return false;

    // Admins and trainers cannot book classes
    if (user.role === 'admin' || user.role === 'trainer') return false;

    const availability = getAvailabilityInfo();
    const userBooked = isUserBooked();

    return (
      scheduledClass.status === 'scheduled' &&
      !availability.isFull &&
      !userBooked &&
      new Date(scheduledClass.startTime) > new Date()
    );
  };

  const canUnbookClass = () => {
    if (!scheduledClass || !user) return false;

    // Admins and trainers cannot unbook classes
    if (user.role === 'admin' || user.role === 'trainer') return false;

    const userBooked = isUserBooked();

    return (
      userBooked &&
      scheduledClass.status !== 'completed' &&
      scheduledClass.status !== 'in-progress' &&
      new Date(scheduledClass.startTime) > new Date()
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!scheduledClass) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Alert severity="error">
            Class not found or you don't have permission to view it.
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/classes')}
            sx={{ mt: 2 }}
          >
            Back to Classes
          </Button>
        </Box>
      </Container>
    );
  }

  const availability = getAvailabilityInfo();
  const userBooked = isUserBooked();

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/classes')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Class Details
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Class Information */}
        <Grid size={{xs:12 , md:8}}>
          <Paper sx={{ p: 3, mb: 3 }}>
            {/* Class Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Box>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {scheduledClass.classType?.name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {scheduledClass.classType?.category && (
                    `${scheduledClass.classType.category.charAt(0).toUpperCase() + scheduledClass.classType.category.slice(1)} • `
                  )}
                  {formatDuration(scheduledClass.startTime, scheduledClass.endTime)}
                </Typography>
              </Box>
              <Chip
                label={scheduledClass.status}
                color={getStatusColor(scheduledClass.status)}
                size="large"
              />
            </Box>

            {/* Class Description */}
            {scheduledClass.classType?.description && (
              <Box mb={3}>
                <Typography variant="body1" paragraph>
                  {scheduledClass.classType.description}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Class Details */}
            <Grid container spacing={3}>
              <Grid size={{xs:12 , sm:6}}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Date"
                      secondary={getDateLabel(scheduledClass.startTime)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <TimeIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Time"
                      secondary={`${formatTime(scheduledClass.startTime)} - ${formatTime(scheduledClass.endTime)}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Trainer"
                      secondary={`${scheduledClass.trainer?.firstName} ${scheduledClass.trainer?.lastName}`}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid size={{xs:12 , sm:6}}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Location"
                      secondary={`${scheduledClass.facility?.name} (${scheduledClass.facility?.type})`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <GroupIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Capacity"
                      secondary={`${availability.enrolled} / ${availability.capacity} enrolled`}
                    />
                  </ListItem>
                  {scheduledClass.classType?.difficulty && (
                    <ListItem>
                      <ListItemIcon>
                        <FitnessIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Difficulty"
                        secondary={scheduledClass.classType.difficulty}
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>
            </Grid>

            {/* Required Equipment */}
            {scheduledClass.classType?.requiredEquipment && scheduledClass.classType.requiredEquipment.length > 0 && (
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>
                  Required Equipment
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {scheduledClass.classType.requiredEquipment.map((equipment, index) => (
                    <Chip key={index} label={equipment} variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            {/* Notes */}
            {scheduledClass.notes && (
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>
                  Additional Notes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {scheduledClass.notes}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Booking Panel - Only show for members */}
        {user && user.role !== 'admin' && user.role !== 'trainer' && (
          <Grid size={{xs:12 , md:4}}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Booking Status
              </Typography>

              {/* User's booking status */}
              {userBooked ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center">
                    <CheckIcon sx={{ mr: 1 }} />
                    You are booked for this class
                  </Box>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center">
                    <InfoIcon sx={{ mr: 1 }} />
                    You are not booked for this class
                  </Box>
                </Alert>
              )}

              {/* Availability status */}
              <Box mb={3}>
                {availability.isFull ? (
                  <Chip label={t('scheduledClass:details.classFull')} color="warning" size="large" sx={{ width: '100%' }} />
                ) : availability.isAlmostFull ? (
                  <Chip
                    label={t('scheduledClass:details.spotsRemaining', { count: availability.available })}
                    color="info"
                    size="large"
                    sx={{ width: '100%' }}
                  />
                ) : (
                  <Chip label={t('scheduledClass:details.spotsAvailable')} color="success" size="large" sx={{ width: '100%' }} />
                )}
              </Box>

              {/* Action Buttons */}
              <Box display="flex" flexDirection="column" gap={2}>
                {canBookClass() && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<BookIcon />}
                    onClick={handleQuickBook}
                    disabled={actionLoading}
                    fullWidth
                  >
                    Book This Class
                  </Button>
                )}

                {canUnbookClass() && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="large"
                    startIcon={<UnbookIcon />}
                    onClick={() => setUnbookingDialogOpen(true)}
                    disabled={actionLoading}
                    fullWidth
                  >
                    Cancel Booking
                  </Button>
                )}

                {scheduledClass.status === 'cancelled' && (
                  <Alert severity="error">
                    <Box display="flex" alignItems="center">
                      <CancelIcon sx={{ mr: 1 }} />
                      This class has been cancelled
                    </Box>
                  </Alert>
                )}

                {scheduledClass.status === 'completed' && (
                  <Alert severity="info">
                    This class has been completed
                  </Alert>
                )}
              </Box>
            </Paper>

            {/* Attendees List */}
            {scheduledClass.attendees && scheduledClass.attendees.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Attendees ({scheduledClass.attendees.length})
                </Typography>
                <List dense>
                  {scheduledClass.attendees.slice(0, 10).map((attendee, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {attendee.user?.firstName?.[0]}{attendee.user?.lastName?.[0]}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={`${attendee.user?.firstName} ${attendee.user?.lastName}`}
                        secondary={attendee.status !== 'booked' ? attendee.status : null}
                      />
                    </ListItem>
                  ))}
                  {scheduledClass.attendees.length > 10 && (
                    <ListItem>
                      <ListItemText
                        primary={`+${scheduledClass.attendees.length - 10} more attendees`}
                        sx={{ textAlign: 'center', fontStyle: 'italic' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>
            )}
          </Grid>
        )}

        {/* Attendees List for Admins/Trainers - Show in full width */}
        {user && (user.role === 'admin' || user.role === 'trainer') && scheduledClass.attendees && scheduledClass.attendees.length > 0 && (
          <Grid size={{xs:12 , md:4}}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Attendees ({scheduledClass.attendees.length})
              </Typography>
              <List dense>
                {scheduledClass.attendees.slice(0, 10).map((attendee, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {attendee.user?.firstName?.[0]}{attendee.user?.lastName?.[0]}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={`${attendee.user?.firstName} ${attendee.user?.lastName}`}
                      secondary={attendee.status !== 'booked' ? attendee.status : null}
                    />
                  </ListItem>
                ))}
                {scheduledClass.attendees.length > 10 && (
                  <ListItem>
                    <ListItemText
                      primary={`+${scheduledClass.attendees.length - 10} more attendees`}
                      sx={{ textAlign: 'center', fontStyle: 'italic' }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Class Booking Dialog */}
      <ClassBookingDialog
        open={bookingDialog.open}
        onClose={handleBookingDialogClose}
        classData={bookingDialog.classData}
        onSuccess={handleBookingSuccess}
      />

      {/* Unbooking Confirmation Dialog */}
      <Dialog open={unbookingDialogOpen} onClose={() => setUnbookingDialogOpen(false)}>
        <DialogTitle>Cancel Class Booking</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to cancel your booking for this class?
          </Typography>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              <strong>Class:</strong> {scheduledClass.classType?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Date:</strong> {getDateLabel(scheduledClass.startTime)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Time:</strong> {formatTime(scheduledClass.startTime)} - {formatTime(scheduledClass.endTime)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnbookingDialogOpen(false)}>
            Keep Booking
          </Button>
          <Button 
            onClick={handleUnbookClass} 
            color="error" 
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading && <CircularProgress size={20} />}
          >
            {actionLoading ? 'Cancelling...' : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ScheduledClassDetailsPage;